// Admin CRUD: categories (list, add, edit, delete)

import { useState } from 'react';
import { useCategories } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Category } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ImageUploadField } from '@/components/store/ImageUploadField';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';

export default function AdminCategories() {
  const queryClient = useQueryClient();
  const { data: categories, isLoading } = useCategories();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [cover_image, setCover_image] = useState('');
  const { toast } = useToast();

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        const { error } = await supabase.from('categories').update({ name, slug, description: description || null, cover_image: cover_image || null }).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('categories').insert({ name, slug, description: description || null, cover_image: cover_image || null });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'categories'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Category updated' : 'Category created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'categories'] });
      toast({ title: 'Category deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setName('');
    setSlug('');
    setDescription('');
    setCover_image('');
    setEditing(null);
  }

  function openEdit(cat: Category) {
    setEditing(cat);
    setName(cat.name);
    setSlug(cat.slug);
    setDescription(cat.description ?? '');
    setCover_image(cat.cover_image ?? '');
    setOpen(true);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Categories</h1>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add category</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Slug</Label>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="dairy-products" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <ImageUploadField label="Cover image" value={cover_image} onChange={setCover_image} folder="categories" />
            <p className="text-muted-foreground text-sm">Thumbnail is auto-generated from cover (resize) when displayed.</p>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name || !slug}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">Thumbnail</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(categories ?? []).map((cat) => (
              <TableRow key={cat.id}>
                <TableCell className="w-[72px]">
                  {cat.cover_image ? (
                    <div className="h-12 w-14 rounded border border-zinc-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                      <StoreThumbnailImg
                        coverUrl={cat.cover_image}
                        size={{ width: 56, height: 48 }}
                        className="max-h-full max-w-full w-auto h-auto object-contain"
                      />
                    </div>
                  ) : (
                    <span className="text-zinc-400 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell>{cat.name}</TableCell>
                <TableCell>{cat.slug}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(cat)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(cat.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
